'use client';
import { useState } from 'react';
import Slider from 'react-slick';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { LeftArrow, RightArrow } from '@repo/ui/src/components/Carousels/CarouselArrows';
import FeatureCarouselCard, { FeatureCardProps } from './_FeatureCarouselCard';
import { BackgroundStyleEnum } from '@repo/ui/src/enums/shared-enums';
import Section from '@repo/ui/src/components/Section';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '@repo/ui/src/utils/appConfig';
import styles from './FeatureCarousel.module.css';

interface FeatureCarouselProps {
  cards: Array<FeatureCardProps>;
  hideNavigationTabs?: boolean;
  background: string;
  lightArrows?: boolean;
  _testID?: string;
}

const FeatureCarousel = ({
  cards,
  hideNavigationTabs,
  background,
  _testID,
}: FeatureCarouselProps) => {
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const isMeater = appConfig.isMeater;

  const topSettings = {
    accessibility: true,
    dots: true,
    infinite: true,
    centerMode: true,
    centerPadding: isMeater ? '0px' : '180px',
    speed: reduceMotion ? 0 : 500,
    slidesToShow: 1,
    arrows: !isMeater,
    slidesToScroll: 1,
    focusOnSelect: true,
    nextArrow: <RightArrow onImage aria-label="Next slide" role="button" arrowSize="40" />,
    prevArrow: <LeftArrow onImage aria-label="Previous slide" role="button" arrowSize="40" />,
    responsive: [
      {
        breakpoint: 960,
        settings: {
          centerPadding: isMeater ? '0px' : '100px',
          arrows: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          centerPadding: isMeater ? '0px' : '25px',
          arrows: false,
        },
      },
    ],
    appendDots: (dots: boolean) => (
      <div>{(isMobile || !hideNavigationTabs || isMeater) && <ul> {dots} </ul>}</div>
    ),
    customPaging: (i: number) => (
      <button
        aria-current={selectedCardIndex === i}
        aria-label={`Go to slide ${i + 1} of ${cards.length}: ${cards[i].toutContent?.heading || 'Slide content'}`}
        onClick={() => setSelectedCardIndex(i)}
        className={classNames('tw-flex tw-flex-col tw-items-center tw-w-full tw-gap-2')}
      >
        {/* Divider bar */}
        <div
          className={classNames(
            'tw-w-full tw-transition-colors',
            isMeater ? 'tw-h-[3px]' : 'tw-h-1 tw-my-6',
            isMeater
              ? selectedCardIndex === i
                ? 'tw-bg-[#c8102e]'
                : 'tw-bg-[#808285]'
              : 'tw-bg-brand-300',
          )}
        />
        {/* Label */}
        <span
          className={classNames(
            'tw-uppercase tw-text-center',
            isMeater
              ? 'tw-font-display tw-font-bold tw-text-sm tw-leading-none tw-transition-colors'
              : 'tra-text-web-heading-h6 tw-text-brand-300',
            isMeater && (selectedCardIndex === i ? 'tw-text-[#1d1d1f]' : 'tw-text-[#808285]'),
          )}
        >
          {cards[i].toutContent?.heading}
        </span>
        {/* Screen reader helper */}
        <span className="tw-absolute tw-w-px tw-h-px tw-p-0 tw-m-[-1px] tw-overflow-hidden tw-whitespace-nowrap tw-border-0">
          {cards[i].toutContent?.heading || `Slide ${i + 1} content`}
        </span>
      </button>
    ),
  };

  return (
    <div
      role="region"
      aria-label={`feature carousel ${_testID ? _testID : ''}`}
      data-test-id={`feature_carousel_${_testID}`}
      className={classNames(
        'feature-carousel tw-relative',
        isMeater ? styles.meaterCarousel : styles.traegerCarousel,
      )}
    >
      <span className="tw-absolute tw-w-px tw-h-px tw-p-0 tw-m-[-1px] tw-overflow-hidden tw-whitespace-nowrap tw-border-0">
        This is a carousel with {cards.length} slides. Use the previous and next buttons to navigate
        between slides, or use the tab key to access the slide navigation tabs. Each slide can be
        selected directly using the tabs below the carousel.
      </span>
      <Section
        className="tw-w-full tw-overflow-hidden"
        white={isMeater || !background || background === BackgroundStyleEnum.White}
        dark={!isMeater && background === BackgroundStyleEnum.TextureDark}
        black={!isMeater && background === BackgroundStyleEnum.Black}
        darkGray={!isMeater && background === BackgroundStyleEnum.DarkGray}
        texture={
          !isMeater &&
          (background === BackgroundStyleEnum.TextureLight ||
            background === BackgroundStyleEnum.TextureDark)
        }
        gutterY
      >
        <Slider {...topSettings}>
          {cards.map((card, i) => (
            <FeatureCarouselCard
              key={card.toutContent?.heading || i}
              {...card}
              alt={card.toutContent?.heading || `slide ${i + 1}`}
              isActive={i === selectedCardIndex}
              index={i}
              totalSlides={cards.length}
            />
          ))}
        </Slider>
      </Section>
    </div>
  );
};

export default FeatureCarousel;
